import { useMutation, useQuery } from 'react-query';
import { RemoteInterviewPracticeRepo } from '@repository/interviewPractices/InterviewPracticesRepo';
import { InterviewQuestionType } from '@components/pass/interview/start/context/interviewContext';
import { InterviewPracticeCreateRq } from '@domain/rq/pass/interview/InterviewPracticeCreateRq';
import { ContinueInterviewPracticeVO } from '@domain/vo/pass/interview/practice/ContinueInterviewPracticeVO';
import InterviewHistoryListDisplayVO from '@domain/vo/pass/interview/history/InterviewHistoryListVO';

const remoteInterviewPracticeRepo = new RemoteInterviewPracticeRepo();

export const keys = ['interview-practices'];

const checkInProgress = async () => {
  const res = await remoteInterviewPracticeRepo.checkInProgress();
  return new ContinueInterviewPracticeVO(res.data);
};

export const useCheckInProgress = (enabled: boolean) => {
  return useQuery([...keys, 'in-progress'], checkInProgress, {
    enabled,
  });
};

const deleteInterviewPractice = async (sn: number) => {
  await remoteInterviewPracticeRepo.deleteInterviewPractice(sn);
};

export const useDeleteInterviewPractice = () => {
  return useMutation((sn: number) => deleteInterviewPractice(sn));
};

const createInterviewPractice = async (selectedQuestionList: InterviewQuestionType[]) => {
  const rq = new InterviewPracticeCreateRq(selectedQuestionList);
  return await remoteInterviewPracticeRepo.createInterviewPractice(rq);
};

export const useCreateInterviewPractice = () => {
  return useMutation((selectedQuestionList: InterviewQuestionType[]) => createInterviewPractice(selectedQuestionList));
};

const readInterviewHistoryList = async () => {
  const { data } = await remoteInterviewPracticeRepo.readInterviewHistoryList();
  return new InterviewHistoryListDisplayVO(data);
};

export const useInterviewHistoryList = () => {
  return useQuery([...keys, 'results'], readInterviewHistoryList);
};

const readPracticeVideo = async (practiceQuestionSn: number) => {
  const { data } = await remoteInterviewPracticeRepo.readPracticeVideo(practiceQuestionSn);
  return data;
};

export const usePracticeVideo = (practiceQuestionSn: number) => {
  return useQuery([...keys, 'questions', 'videos', practiceQuestionSn], () => readPracticeVideo(practiceQuestionSn), {
    enabled: !!practiceQuestionSn,
  });
};

const readQuestionAnswerResult = async (practiceQuestionSn: number) => {
  const { data } = await remoteInterviewPracticeRepo.readQuestionAnswerResult(practiceQuestionSn);
  return data;
};

export const useQuestionAnswerResult = (practiceQuestionSn: number) => {
  return useQuery(
    [...keys, 'questions', 'results', practiceQuestionSn],
    () => readQuestionAnswerResult(practiceQuestionSn),
    {
      enabled: !!practiceQuestionSn,
    },
  );
};
